import { useQuery } from '@tanstack/react-query'

import type { OpportunityType } from '@/types/Opportunity'

import { fetchSimilarOpportunities } from './fetcher'
import { similarOpportunityKeys } from './keys'

/**
 * Headless hook for fetching similar opportunities
 *
 * Type-agnostic hook that works with any opportunity type.
 * Returns react-query result with data, loading, and error states.
 *
 * @param type - The opportunity type (federal_contract, federal_grant, etc.)
 * @param id - The opportunity ID to find similar opportunities for
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSimilarOpportunities('federal_contract', contractId)
 * ```
 */
export function useSimilarOpportunities(type: OpportunityType, id: string) {
  return useQuery({
    queryKey: similarOpportunityKeys.detail(type, id),
    queryFn: () => fetchSimilarOpportunities(type, id),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: id !== '',
  })
}
